<template>
  <div>
    <div class="page-header">
      <div class="page-leftheader">
        <div class="headline">
          <h4 class="page-title text-primary">Rundnachricht an alle Nutzer</h4>
          <p class="page-subtitle mb-0">
            Verschickt eine Mitteilung auf die Handys aller Nutzer, die
            Benachrichtigungen eingeschaltet haben.
          </p>
        </div>
      </div>
    </div>

    <!--
      Bestätigung nach dem Absenden. Bleibt stehen, bis die nächste Nachricht
      geschrieben wird — ein Hinweis, der nach Sekunden verschwindet, wird bei
      einer Aktion dieser Tragweite zu leicht übersehen.
    -->
    <!--
      WAR FALSCH: Hier stand schlicht "Empfänger: X Nutzer" — dieselbe Zahl,
      die die Vorschau-Kachel rechts beim Öffnen der Seite geladen hat
      (recipients.reachable). Sie sagt nur "so viele könnten erreicht
      werden", NICHT wie viele die Nachricht wirklich bekommen haben — dazu
      läuft erst noch der ganze Versand mit abgelaufenen Geräteschlüsseln,
      abgemeldeten Geräten und Netzfehlern dazwischen. In der Tabelle
      "Zuletzt verschickt" darunter stand für dieselbe Nachricht regelmäßig
      eine andere Zahl (die tatsächlichen Zustellungen), ohne dass der
      Unterschied erklärt war — das sah wie ein Widerspruch aus. Jetzt heißt
      die Zahl hier "voraussichtlich" und der Text erklärt, wo die
      bestätigte Zahl steht.
    -->
    <div class="alert alert-success d-flex align-items-start" v-if="lastSent">
      <i class="fe fe-check-circle mr-3 mt-1 broadcast-alert-icon"></i>
      <div>
        <strong>Die Rundnachricht wurde abgeschickt.</strong>
        <div class="mt-1">
          Voraussichtlich erreichte Nutzer: <strong>{{ lastSent.recipients }}</strong> &middot;
          abgeschickt um {{ lastSent.time }}
        </div>
        <div class="text-muted mt-1">„{{ lastSent.text }}“</div>
        <div class="text-muted mt-2 small">
          Der Versand läuft auf dem Server weiter und braucht bei vielen Nutzern
          einige Minuten. Die tatsächlich erreichte Anzahl steht danach in der
          Tabelle „Zuletzt verschickt" unten — sie kann von der Zahl hier oben
          abweichen (abgelaufene Geräteschlüssel, Netzfehler). Sie können diese
          Seite bedenkenlos verlassen.
        </div>
      </div>
    </div>

    <div class="row">
      <!-- Linke Spalte: die Nachricht -->
      <div class="col-lg-7">
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Nachricht verfassen</h3>
          </div>
          <div class="card-body">
            <div class="form-group">
              <label class="form-label" for="broadcastTextEn">
                Text auf Englisch
                <span class="text-danger">*</span>
              </label>
              <textarea
                id="broadcastTextEn"
                class="form-control"
                rows="3"
                maxlength="200"
                placeholder="Beispiel: New meetups near you this weekend!"
                v-model="message.en"
                v-bind:class="{ 'is-invalid': showValidation && !hasText(message.en) }"
              ></textarea>
              <!--
                WAR FALSCH: Hier stand „Alle Nutzer, deren Sprache nicht
                hinterlegt ist, bekommen diesen Text." Diese Gruppe gibt es
                gar nicht. Das Feld `language` im Nutzer-Datenmodell hat den
                Vorgabewert 'de' (ETSBackend/models/User.js) — jedes Konto
                trägt also eine Sprache, ob jemand sie gesetzt hat oder nicht,
                und die App schickt keine eigene mit. Der Satz beschrieb damit
                einen leeren Fall und verschleierte den echten: Der Server
                nimmt den Text in der Kontosprache nur, wenn dafür oben auch
                wirklich etwas steht, und weicht sonst auf Englisch aus
                (ETSBackend/controllers/v1/adminController.js,
                sendNotificationToAllUsers). Bleibt das deutsche Feld leer,
                geht der englische Text damit an sämtliche deutschen Nutzer —
                genau das, was die alte Beschriftung nicht erkennen ließ.

                Bewusst OHNE feste Kontozahl in diesem Kommentar: Eine
                gemessene Zahl veraltet mit dem nächsten angelegten Konto (in
                der Prüfdatenbank stand hier zwischenzeitlich 35, kurz darauf
                38). Wer nachzählen will:
                  db.users.aggregate([{$group:{_id:'$language',n:{$sum:1}}}])
              -->
              <div class="d-flex justify-content-between mt-1">
                <small class="text-muted">
                  Pflichtfeld. Diesen Text bekommt jeder Nutzer, für dessen
                  Kontosprache unten kein Text steht — bleibt das deutsche
                  Feld leer, also praktisch alle.
                </small>
                <small v-bind:class="counterClass(message.en)">
                  {{ length(message.en) }} / {{ RECOMMENDED_LENGTH }}
                </small>
              </div>
              <div class="invalid-feedback d-block" v-if="showValidation && !hasText(message.en)">
                Ohne englischen Text lässt sich die Nachricht nicht verschicken.
              </div>
            </div>

            <div class="form-group mb-0">
              <label class="form-label" for="broadcastTextDe">Text auf Deutsch</label>
              <textarea
                id="broadcastTextDe"
                class="form-control"
                rows="3"
                maxlength="200"
                placeholder="Beispiel: Dieses Wochenende sind neue Treffen in deiner Nähe!"
                v-model="message.de"
              ></textarea>
              <!--
                WAR FALSCH: "Freiwillig" klang, als könnte man dieses Feld
                gefahrlos leer lassen. Tatsächlich steht jedes Konto auf
                Deutsch, solange niemand es umstellt — 'de' ist der
                Vorgabewert im Nutzer-Datenmodell (ETSBackend/models/User.js),
                und die App schickt keine Sprache mit. Bleibt das Feld leer,
                bekommen GENAU DIESE Nutzer trotzdem den englischen Text, weil
                der Server ohne deutschen Text auf Englisch ausweicht
                (ETSBackend/controllers/v1/adminController.js). Wer nur
                Englisch ausfüllt, verschickt also faktisch eine englische
                Nachricht an praktisch alle Nutzer.
              -->
              <div class="d-flex justify-content-between mt-1">
                <small class="text-muted">
                  Empfohlen. Ohne diesen Text bekommen auch Nutzer mit
                  deutscher Spracheinstellung den englischen Text zu sehen.
                </small>
                <small v-bind:class="counterClass(message.de)">
                  {{ length(message.de) }} / {{ RECOMMENDED_LENGTH }}
                </small>
              </div>
            </div>
          </div>
          <div class="card-footer d-flex align-items-center justify-content-between">
            <small class="text-muted">
              <i class="fe fe-alert-triangle mr-1"></i>
              Eine abgeschickte Nachricht lässt sich nicht zurückholen.
            </small>
            <!--
              Bewusst klickbar, auch wenn der Text fehlt: ein gesperrter Knopf
              reagiert gar nicht und lässt den Nutzer ohne Erklärung zurück.
              Gesperrt wird nur während des Sendens.
            -->
            <a
              class="btn btn-primary"
              v-bind:class="{ 'is-incomplete': !canSend, 'is-locked': isSending }"
              @click="openConfirmDialog()"
            >
              <i class="fe fe-send mr-1"></i>
              Abschicken
            </a>
          </div>
        </div>
      </div>

      <!-- Rechte Spalte: Empfänger und Vorschau -->
      <div class="col-lg-5">
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Empfänger</h3>
          </div>
          <div class="card-body">
            <div class="d-flex align-items-baseline">
              <h1 class="mb-0 text-primary">{{ recipients.reachable }}</h1>
              <span class="ml-2 text-muted">Nutzer werden erreicht</span>
            </div>
            <div class="text-muted mt-3 small" v-if="recipients.enabled > recipients.reachable">
              {{ recipients.enabled - recipients.reachable }} weitere haben
              Benachrichtigungen zwar eingeschaltet, aber derzeit kein
              erreichbares Gerät hinterlegt.
            </div>
            <div class="text-muted mt-3 small">
              Nutzer, die Benachrichtigungen ausgeschaltet haben, bekommen nichts.
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-header">
            <h3 class="card-title">So sieht es auf dem Handy aus</h3>
          </div>
          <div class="card-body">
            <div class="push-preview">
              <div class="push-preview-head">
                <img
                  src="/images/brand/favicon.png"
                  class="push-preview-icon"
                  alt=""
                />
                <span class="push-preview-app">Enjoy the Street</span>
                <span class="push-preview-time">jetzt</span>
              </div>
              <div class="push-preview-body">
                {{ previewText }}
              </div>
            </div>
            <small class="text-muted d-block mt-2">
              Vorschau des deutschen Textes, ersatzweise des englischen. Längere
              Texte kürzt das Handy selbstständig.
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- Verlauf: Beleg, dass frühere Nachringen tatsächlich herausgingen -->
    <div class="card">
      <div class="card-header">
        <h3 class="card-title">Zuletzt verschickt</h3>
      </div>
      <div class="table-responsive">
        <table class="table card-table table-vcenter text-nowrap" v-if="broadcasts.length > 0">
          <thead>
            <tr>
              <th>Zeitpunkt</th>
              <th>Deutsch</th>
              <th>Englisch</th>
              <th>Erreichte Nutzer</th>
              <th>Abgeschickt von</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="broadcast in broadcasts" v-bind:key="broadcast._id">
              <td>{{ formatDate(broadcast.createdAt) }}</td>
              <td class="broadcast-text" v-bind:title="broadcast.message.de">
                {{ broadcast.message.de || '—' }}
              </td>
              <td class="broadcast-text" v-bind:title="broadcast.message.en">
                {{ broadcast.message.en || '—' }}
              </td>
              <td>{{ broadcast.usersNotified }}</td>
              <td>{{ broadcast.user ? broadcast.user.userName : 'Unbekannt' }}</td>
            </tr>
          </tbody>
        </table>
        <div class="card-body text-center text-muted" v-else>
          Bisher wurde keine Rundnachricht verschickt.
        </div>
      </div>
    </div>

    <!-- Sicherheitsabfrage -->
    <v-dialog v-model="confirmDialog" width="500" persistent>
      <v-card>
        <v-card-title class="card">
          <h4 class="page-title text-primary">Wirklich an alle abschicken?</h4>
        </v-card-title>
        <v-spacer></v-spacer>
        <v-card-text v-if="!isSending">
          Diese Nachricht geht an <strong>{{ recipients.reachable }}</strong>
          Nutzer und lässt sich nicht zurückholen.
          <div class="push-preview mt-3">
            <div class="push-preview-head">
              <img src="/images/brand/favicon.png" class="push-preview-icon" alt="" />
              <span class="push-preview-app">Enjoy the Street</span>
              <span class="push-preview-time">jetzt</span>
            </div>
            <div class="push-preview-body">{{ previewText }}</div>
          </div>
        </v-card-text>
        <v-card-text v-else>
          Die Nachricht wird verschickt …
          <div class="lds-spinner">
            <div></div><div></div><div></div><div></div><div></div><div></div>
            <div></div><div></div><div></div><div></div><div></div><div></div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <a
            class="btn btn-secondary btn-pill mr-2"
            v-bind:class="{ 'is-locked': isSending }"
            @click="closeConfirmDialog()"
            >Abbrechen</a
          >
          <a
            class="btn btn-primary btn-pill"
            v-bind:class="{ 'is-locked': isSending }"
            @click="sendNotification()"
            >Ja, abschicken</a
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Ab etwa dieser Länge kürzen die Betriebssysteme den Text in der
// Benachrichtigung — der Zähler warnt vorher.
const RECOMMENDED_LENGTH = 150;

export default {
  components: {},

  data() {
    return {
      RECOMMENDED_LENGTH,

      message: {
        de: '',
        en: '',
      },
      recipients: {
        enabled: 0,
        reachable: 0,
      },
      broadcasts: [],
      // Belegt nach dem Versand, füttert das Erfolgsbanner
      lastSent: null,
      showValidation: false,
      confirmDialog: false,
      isSending: false,
    };
  },

  computed: {
    canSend() {
      return this.hasText(this.message.en) && !this.isSending;
    },

    previewText() {
      if (this.hasText(this.message.de)) return this.message.de;
      if (this.hasText(this.message.en)) return this.message.en;
      return 'Hier erscheint der Text Ihrer Nachricht.';
    },
  },

  mounted() {
    this.fetchRecipients();
    this.fetchBroadcasts();
  },

  methods: {
    hasText(value) {
      return value != null && value.trim().length > 0;
    },

    length(value) {
      return value == null ? 0 : value.length;
    },

    counterClass(value) {
      return this.length(value) > RECOMMENDED_LENGTH ? 'text-danger' : 'text-muted';
    },

    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    fetchRecipients() {
      const _this = this;
      this.$axios
        .get('/broadcast/recipients')
        .then(function (response) {
          if (response && response.data && response.data.data) {
            _this.recipients = response.data.data;
          }
        })
        .catch(function (error) {
          console.error(error);
        });
    },

    fetchBroadcasts() {
      const _this = this;
      this.$axios
        .get('/broadcast/all/20')
        .then(function (response) {
          if (response && response.data && response.data.data) {
            _this.broadcasts = response.data.data;
          }
        })
        .catch(function (error) {
          console.error(error);
        });
    },

    openConfirmDialog() {
      this.showValidation = true;
      if (!this.canSend) {
        this.$noty.error('Bitte zuerst den englischen Text ausfüllen.');
        return;
      }
      this.confirmDialog = true;
    },

    closeConfirmDialog() {
      if (this.isSending) return;
      this.confirmDialog = false;
    },

    sendNotification() {
      // Der Dialog bleibt während des Sendens offen; ohne diese Sperre löst
      // ein zweiter Klick eine zweite Rundnachricht aus.
      if (this.isSending || !this.canSend) return;

      const _this = this;
      const sentText = this.previewText;
      const body = { messages: this.message };
      this.isSending = true;

      this.$axios
        .post('/user/notifyAll', body)
        .then(function (response) {
          _this.lastSent = {
            text: sentText,
            recipients: _this.recipients.reachable,
            time: _this.formatDate(new Date()),
          };
          if (response && response.data && response.data.message) {
            _this.$noty.success(response.data.message);
          } else {
            _this.$noty.success('Die Rundnachricht wurde abgeschickt.');
          }

          _this.message = { de: '', en: '' };
          _this.showValidation = false;
          _this.confirmDialog = false;
          _this.isSending = false;

          // Der Server legt den Eintrag erst nach dem Versand an; kurz warten,
          // damit der Verlauf die neue Nachricht bereits enthält.
          setTimeout(_this.fetchBroadcasts, 2000);
        })
        .catch(function (error) {
          const serverMessage =
            error && error.response && error.response.data && error.response.data.message;
          _this.$noty.error(
            serverMessage || 'Die Nachricht konnte nicht verschickt werden.',
          );
          console.error(error);
          _this.confirmDialog = false;
          _this.isSending = false;
        });
    },
  },
};
</script>

<style scoped>
.v-dialog > .v-card > .v-card__actions {
  padding: 8px 16px;
}

/* Die Gestaltung des Erklärtexts steht jetzt einmal zentral in
   `views/App.vue`. Hier stand dieselbe Regel ein drittes Mal — identisch auch
   in NewsMessages.vue und Reported.vue. Drei Kopien bedeuten: Wer die Farbe
   ändert, ändert sie an einer Stelle und wundert sich über die anderen zwei. */

.broadcast-alert-icon {
  font-size: 20px;
}

/* Nachbau einer Benachrichtigung, damit die Wirkung vor dem Absenden sichtbar ist */
.push-preview {
  background: #f5f6fa;
  border: 1px solid #e8ebf3;
  border-radius: 14px;
  padding: 10px 12px;
}

.push-preview-head {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.push-preview-icon {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  margin-right: 6px;
}

.push-preview-app {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #76839a;
}

.push-preview-time {
  font-size: 11px;
  color: #9aa4b8;
  margin-left: auto;
}

.push-preview-body {
  font-size: 14px;
  color: #2a3142;
  word-break: break-word;
}

/* Lange Texte sprengen sonst die Tabelle */
.broadcast-text {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Unvollständig: sichtbar zurückgenommen, aber ansprechbar */
.btn.is-incomplete {
  opacity: 0.65;
}

/* Während des Sendens wirklich gesperrt, sonst geht die Nachricht doppelt raus */
.btn.is-locked {
  pointer-events: none;
  opacity: 0.55;
}
</style>
